import { AfterViewInit, ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgOption, NgSelectModule } from '@ng-select/ng-select';

import { InputComponent } from '@shared/components/input/input.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { MultiSelectDropdownComponent } from '@shared/components/multi-select-dropdown/multi-select-dropdown.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { OverlayService } from '@shared/services/overlay.service';
import { PurchaseOrdersQuery } from '@shared/store/purchase-orders/purchase-orders.query';
import { Option } from '@shared/types/components.type';

export interface RequestChangeOrderData {
  po_id: string;
  poDropdownItems: Option[];
  userDropdownItems: Option[];
  vendorDropdownItems: Option[];
  vendorId: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    FormErrorDirective,
    ModalComponent,
    InputComponent,
    MultiSelectDropdownComponent,
  ],
  selector: 'aux-po-co-request-modal',
  templateUrl: './po-co-request-modal.component.html',
})
export class PoCoRequestModalComponent implements AfterViewInit {
  readonly overlayService = inject(OverlayService);
  readonly ref = inject(CustomOverlayRef);

  private readonly fb = inject(FormBuilder);
  private readonly purchaseOrdersQuery = inject(PurchaseOrdersQuery);

  poNumberItems = signal<Option[]>([]);
  userItems = signal<Option[]>([]);
  vendorItems = signal<Option[]>([]);

  readonly form = this.fb.group({
    notes: '',
    po_number: ['', Validators.required],
    users: [null, Validators.required],
    vendor: ['', Validators.required],
  });

  ngAfterViewInit(): void {
    if (this.ref.data) {
      this.form.controls.po_number.setValue(this.ref.data.po_id);
      this.form.controls.vendor.setValue(this.ref.data.vendorId);
      this.form.controls.vendor.disable();
    }
  }

  constructor() {
    if (this.ref.data) {
      this.poNumberItems.set(this.ref.data.poDropdownItems);
      this.vendorItems.set(this.ref.data.vendorDropdownItems);
      this.userItems.set(this.ref.data.userDropdownItems);
    }
  }

  onPONumberChange(option: NgOption): void {
    const po = this.purchaseOrdersQuery.$selectAllSorted()?.find((po) => po.id === option.value);

    if (po) {
      this.form.controls.vendor.setValue(po.organization_id);
    }
  }

  submitForm() {
    if (this.form.valid) {
      this.ref.close({
        notes: this.form.controls.notes.value,
        po_option: this.form.controls.po_number.value
          ? this.poNumberItems().find((item) => item.value === this.form.controls.po_number.value)
          : ({} as Option),
        users: this.form.controls.users.value,
        vendor: this.form.controls.vendor.value,
      });
    }
  }
}
